import { Activity, AlertCircle, RefreshCw, LogOut, Shield, User, Bot, Zap, Target, Trophy, FlaskConical, GraduationCap, BarChart3, Calculator, Bell, Calendar, Lightbulb, Wallet, BookOpen, Cpu, HelpCircle, ChevronDown, MoreHorizontal, Building2, MessageCircle, Star } from 'lucide-react';
import { format } from 'date-fns';
import { useState, useEffect, Suspense, lazy } from 'react';
import StockCard from './StockCard';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

// Core components - loaded immediately
import MarketRegimeBanner from './MarketRegimeBanner';
import TradingPsychologyTips from './TradingPsychologyTips';
import DailyBriefingCard from './DailyBriefingCard';
// REMOVED: import OnboardingChecklist from './OnboardingChecklist';
// REMOVED: import NewsTicker from './NewsTicker';
// REMOVED: import NotificationBell from './NotificationBell';
import { useBookmarkedTabs, TabBookmarkButton, MoreTabsDropdown, ALL_TABS } from './BookmarkableTabs';

// Lazy loaded components - loaded on demand
const AlertPanel = lazy(() => import('./AlertPanel'));
const SettingsPanel = lazy(() => import('./SettingsPanel'));
const OptionsFlowPanel = lazy(() => import('./OptionsFlowPanel'));
const OptionsSweepsPanel = lazy(() => import('./OptionsSweepsPanel'));
const MarketOverview = lazy(() => import('./MarketOverview'));
const DarkPoolPanel = lazy(() => import('./DarkPoolPanel'));
// REMOVED: const SmartMoneyConfluence = lazy(() => import('./SmartMoneyConfluence'));
const AIChatWidget = lazy(() => import('./AIChatWidget'));
const DailyWatchlist = lazy(() => import('./DailyWatchlist'));
const Leaderboard = lazy(() => import('./Leaderboard'));
const Backtesting = lazy(() => import('./Backtesting'));
const TradeJournal = lazy(() => import('./TradeJournal'));
const EarningsCalendar = lazy(() => import('./EarningsCalendar'));
const AdminPanel = lazy(() => import('./AdminPanel'));
// REMOVED: Paper Trading components
// const PaperTradingDashboard = lazy(() => import('./PaperTradingDashboard'));
// const PaperTradingEnhanced = lazy(() => import('./PaperTradingEnhanced'));
const LearningCenter = lazy(() => import('./LearningCenter'));
const StockAnalyzer = lazy(() => import('./StockAnalyzer'));
const AdvancedScanner = lazy(() => import('./AdvancedScanner'));
// REMOVED: Tools components
// const PositionCalculator = lazy(() => import('./PositionCalculator'));
// const RiskDashboard = lazy(() => import('./RiskDashboard'));
// REMOVED: Journal component
// const EnhancedTradeJournal = lazy(() => import('./EnhancedTradeJournal'));
const SmartAlertSystem = lazy(() => import('./SmartAlertSystem'));
const EconomicCalendar = lazy(() => import('./EconomicCalendar'));
const VWAPRejectionAlerts = lazy(() => import('./VWAPRejectionAlerts'));
// REMOVED: Paper Trading V2
// const PaperTradingV2 = lazy(() => import('./PaperTradingV2'));
// REMOVED: Discipline components
// const TradingDiscipline = lazy(() => import('./TradingDiscipline'));
// const DailyReview = lazy(() => import('./DailyReview'));
const MarketSentimentV2 = lazy(() => import('./MarketSentimentV2'));
// REMOVED: Auto Trade component
// const AutoTradeOptions = lazy(() => import('./AutoTradeOptions'));
const OnboardingTour = lazy(() => import('./OnboardingTour'));
const UserGuide = lazy(() => import('./UserGuide'));
const StructuredTradePicks = lazy(() => import('./StructuredTradePicks'));
const TradeScoreQueue = lazy(() => import('./TradeScoreQueue'));
// REMOVED: Pro Auto Trading
// const ProAutoTrading = lazy(() => import('./ProAutoTrading'));
const AISignalsPanel = lazy(() => import('./AISignalsPanel'));
const SentimentAlerts = lazy(() => import('./SentimentAlerts'));
const SwingTrades = lazy(() => import('./SwingTrades'));
const LiquiditySweepAlerts = lazy(() => import('./LiquiditySweepAlerts'));
// REMOVED: const WhaleAlerts = lazy(() => import('./WhaleAlerts'));
const SetupAlerts = lazy(() => import('./SetupAlerts'));
const CandlePatternAlerts = lazy(() => import('./CandlePatternAlerts'));
const PatternBacktest = lazy(() => import('./PatternBacktest'));
const ComboBacktest = lazy(() => import('./ComboBacktest'));
const WeeklyWatchlist = lazy(() => import('./WeeklyWatchlist'));
const DailyCandleWatchlist = lazy(() => import('./DailyCandleWatchlist'));
const CatalystScanner = lazy(() => import('./CatalystScanner'));
const BreakRetestScanner = lazy(() => import('./BreakRetestScanner'));
const GapScanner = lazy(() => import('./GapScanner'));
const BreakoutSignals = lazy(() => import('./BreakoutSignals'));
const CongressTrades = lazy(() => import('./CongressTrades'));
const FundamentalScanner = lazy(() => import('./FundamentalScanner'));
const HighConvictionAlerts = lazy(() => import('./HighConvictionAlerts'));
const InsiderTrading = lazy(() => import('./InsiderTrading'));
const InstitutionalTrades = lazy(() => import('./InstitutionalTrades'));
const MarketRecap = lazy(() => import('./MarketRecap'));
const StockPredictions = lazy(() => import('./StockPredictions'));
// REMOVED: const PolymarketPanel = lazy(() => import('./PolymarketPanel'));
const OptionsSellerPanel = lazy(() => import('./OptionsSellerPanel'));
const MomentumScanner = lazy(() => import('./MomentumScanner'));
const PennyRunnerAlerts = lazy(() => import('./PennyRunnerAlerts'));
const AMDPhaseScanner = lazy(() => import('./AMDPhaseScanner'));
const SMCPatternAlerts = lazy(() => import('./SMCPatternAlerts'));
const InvestmentPortfolios = lazy(() => import('./InvestmentPortfolios'));
const IndiaMarket = lazy(() => import('./IndiaMarket'));
const FibLevels = lazy(() => import('./FibLevels'));
const ConfluenceAnalyzer = lazy(() => import('./ConfluenceAnalyzer'));
const SignalTracker = lazy(() => import('./SignalTracker'));
const LiveSignalTracker = lazy(() => import('./LiveSignalTracker'));
const OptionsTradeTracker = lazy(() => import('./OptionsTradeTracker'));
const OIStrikeHeatmap = lazy(() => import('./OIStrikeHeatmap'));
const OIChangeTracker = lazy(() => import('./OIChangeTracker'));
const ImpliedMoveCalculator = lazy(() => import('./ImpliedMoveCalculator'));
const StockAnalysisCard = lazy(() => import('./StockAnalysisCard'));
const TopOpportunitiesScanner = lazy(() => import('./TopOpportunitiesScanner'));
const CommunityChat = lazy(() => import('./CommunityChat'));
// REMOVED: Live Trade component
// const AlpacaLiveTrading = lazy(() => import('./AlpacaLiveTrading'));
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { TabLoadingSkeleton } from './LoadingSkeleton';

// Suspense wrapper for lazy components
const LazyTab = ({ children }) => (
  <Suspense fallback={<TabLoadingSkeleton />}>
    {children}
  </Suspense>
);

export default function Dashboard({ stocks, alerts, loading, lastUpdate, onRefresh, isDemo, onExitDemo }) {
  const { user, logout, isAdmin } = useAuth();
  const [watchlist, setWatchlist] = useState(['QQQ', 'TSLA', 'NVDA', 'AAPL']);
  const [activeTab, setActiveTab] = useState('dailypicks');
  const [showChecklist, setShowChecklist] = useState(false);
  const [currentRegime, setCurrentRegime] = useState(null);
  
  // Bookmarkable tabs hook - pass isAdmin to filter restricted tabs
  const { bookmarkedTabs, toggleBookmark, isBookmarked, getBookmarkedTabsData, getNonBookmarkedTabs } = useBookmarkedTabs(isAdmin);

  useEffect(() => {
    // Show checklist if onboarding not dismissed
    const dismissed = localStorage.getItem('traderelax_checklist_dismissed');
    const completed = localStorage.getItem('traderelax_onboarding_complete');
    if (!dismissed && completed) {
      setShowChecklist(true);
    }
  }, []);

  const handleOnboardingComplete = () => {
    setShowChecklist(true);
  };

  const handleChecklistNavigate = (tab) => {
    setActiveTab(tab);
  };

  const handleWatchlistChange = (newWatchlist) => {
    setWatchlist(newWatchlist);
    // Trigger refresh to load new stocks
    onRefresh();
  };

  // Filter stocks based on watchlist - with null safety
  const watchlistStocks = (stocks || []).filter(stock => watchlist.includes(stock?.ticker));

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      {/* Demo Mode Banner */}
      {isDemo && (
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-3" data-testid="demo-banner">
          <Zap className="w-4 h-4" />
          <span><strong>Demo Mode</strong> - Exploring with sample data. Sign up to access real-time features!</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={onExitDemo}
            className="bg-white/20 hover:bg-white/30 text-white border-0 h-7 text-xs"
            data-testid="exit-demo-btn"
          >
            Sign Up Free
          </Button>
        </div>
      )}
      
      <header className="h-14 border-b border-[#2A2E39] bg-[#0B0E14]/75 backdrop-blur-xl sticky top-0 z-50 flex items-center px-6" data-testid="dashboard-header">
        <div className="flex-1 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-[#2962FF]" strokeWidth={1.5} />
            <h1 className="text-xl font-semibold tracking-tight text-white">TradeRelax</h1>
          </div>
          
          <div className="flex items-center gap-4">
            {/* User Info */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-sm bg-[#131722] border border-[#2A2E39] flex items-center justify-center">
                {isAdmin ? (
                  <Shield className="w-4 h-4 text-[#2962FF]" />
                ) : (
                  <User className="w-4 h-4 text-[#2962FF]" />
                )}
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-medium text-[#D1D4DC]">{user?.full_name}</div>
                <div className="text-xs text-[#787B86]">{isAdmin ? 'Admin' : 'Trader'}</div>
              </div>
            </div>

            <span className="text-xs text-[#787B86] hidden md:block font-mono">
              {format(lastUpdate, 'HH:mm:ss')}
            </span>
            
            {/* REMOVED: Notification Bell */}
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRefresh}
              disabled={loading}
              data-testid="refresh-button"
              className="rounded-sm h-8 border-[#2A2E39] bg-transparent text-[#D1D4DC] hover:bg-[#131722] hover:border-[#3A3E49]"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            
            <Button 
              variant="ghost" 
              size="sm"
              onClick={logout}
              className="text-[#787B86] hover:text-[#F23645] hover:bg-[#F23645]/10"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* REMOVED: News Ticker - TV style scrolling news */}

      <main className="p-4 md:p-6">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* MOVED: Market Regime, Psychology Tips, AI Briefing now only show on Daily Picks tab */}

          {/* All Tabs Section */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
              <div className="flex items-center gap-1">
                {/* Bookmarked Tabs */}
                <TabsList className="inline-flex w-max gap-1 p-1 bg-[#131722] border border-[#2A2E39]">
                  {getBookmarkedTabsData().map(tab => (
                    <TabsTrigger 
                      key={tab.id}
                      value={tab.id} 
                      className="text-xs md:text-sm whitespace-nowrap group relative text-[#787B86] data-[state=active]:bg-[#2962FF] data-[state=active]:text-white"
                    >
                      <span className="mr-1">{tab.icon}</span>
                      {tab.label}
                      <TabBookmarkButton 
                        tabId={tab.id}
                        isBookmarked={true}
                        onToggle={toggleBookmark}
                      />
                    </TabsTrigger>
                  ))}
                  
                  {/* Admin Tab inside TabsList */}
                  {isAdmin && (
                    <TabsTrigger value="admin" className="text-red-500 text-xs md:text-sm whitespace-nowrap">
                      <Shield className="w-3 h-3 md:w-4 md:h-4 mr-1" />Admin
                    </TabsTrigger>
                  )}
                </TabsList>
                
                {/* More Tabs Dropdown */}
                <MoreTabsDropdown 
                  tabs={getNonBookmarkedTabs()}
                  onSelect={(tabId) => {
                    setActiveTab(tabId);
                  }}
                  currentTab={activeTab}
                  onBookmark={toggleBookmark}
                />
              </div>
            </div>

            {/* Learn Tab - First for new traders */}
            <TabsContent value="learn" className="mt-4">
              <div className="max-w-3xl mx-auto">
                <LazyTab><LearningCenter /></LazyTab>
              </div>
            </TabsContent>

            <TabsContent value="dailypicks" className="mt-4" keepMounted>
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Market Regime Banner - Only on Daily Picks */}
                <MarketRegimeBanner onRegimeChange={setCurrentRegime} />

                {/* Trading Psychology Tips - Only on Daily Picks */}
                <TradingPsychologyTips />

                {/* AI Daily Briefing - Only on Daily Picks */}
                <div data-tour="ai-briefing">
                  <DailyBriefingCard />
                </div>

                {/* Today's Trade Plans */}
                <LazyTab><StructuredTradePicks /></LazyTab>
                
                {/* AI Market Analysis */}
                <div className="border-t border-zinc-800 pt-6">
                  <h3 className="text-lg font-semibold text-zinc-400 mb-4">AI Market Analysis</h3>
                  <LazyTab><DailyWatchlist /></LazyTab>
                </div>

                {/* Market Overview - Today's Market, My Watchlist, Market News - Only on Daily Picks */}
                <LazyTab><MarketOverview onWatchlistChange={handleWatchlistChange} /></LazyTab>
              </div>
            </TabsContent>

            {/* Gap Scanner - FVG & BAG */}
            <TabsContent value="gapscanner" className="mt-4">
              <div className="max-w-6xl mx-auto">
                <LazyTab><GapScanner /></LazyTab>
              </div>
            </TabsContent>

            {/* Breakout Signals - PDH/PDL + PMH/PML */}
            <TabsContent value="breakoutsignals" className="mt-4">
              <div className="max-w-6xl mx-auto">
                <LazyTab><BreakoutSignals /></LazyTab>
              </div>
            </TabsContent>

            {/* Swing Trades - Prophitcy 4-setup daily scanner + trade plans */}
            <TabsContent value="swingtrades" className="mt-4" keepMounted>
              <div className="max-w-6xl mx-auto">
                <LazyTab><SwingTrades /></LazyTab>
              </div>
            </TabsContent>

            {/* REMOVED: Whale Alerts - Unified Smart Money Detection */}

            {/* AI Signals - Unified Signal Engine */}
            <TabsContent value="aisignals" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><AISignalsPanel /></LazyTab>
              </div>
            </TabsContent>

            {/* First 15-Min A+ Setup Alerts */}
            <TabsContent value="setupalerts" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><SetupAlerts /></LazyTab>
              </div>
            </TabsContent>

            {/* Candlestick Pattern Alerts - Hammer, Shooting Star, etc. */}
            <TabsContent value="candlepatterns" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><CandlePatternAlerts /></LazyTab>
              </div>
            </TabsContent>

            {/* Pattern Backtest - 7 Year Historical Analysis */}
            <TabsContent value="patternbacktest" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><PatternBacktest /></LazyTab>
              </div>
            </TabsContent>

            {/* Combo Backtest - Pattern Combinations & Alerts */}
            <TabsContent value="combobacktest" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><ComboBacktest /></LazyTab>
              </div>
            </TabsContent>

            {/* Weekly Watchlist - Trading Plan for the Week */}
            <TabsContent value="weeklywatchlist" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><WeeklyWatchlist /></LazyTab>
              </div>
            </TabsContent>

            {/* Daily Candle Watchlist - Swing Trading Opportunities */}
            <TabsContent value="dailycandlewatchlist" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><DailyCandleWatchlist /></LazyTab>
              </div>
            </TabsContent>

            {/* Catalyst Scanner - Find DELL-like setups */}
            <TabsContent value="catalystscanner" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><CatalystScanner /></LazyTab>
              </div>
            </TabsContent>

            {/* Break & Retest Scanner - Price Action Strategy */}
            <TabsContent value="breakretest" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><BreakRetestScanner /></LazyTab>
              </div>
            </TabsContent>

            {/* Congress Trades - Track Politician Stock Trades */}
            <TabsContent value="congress" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><CongressTrades /></LazyTab>
              </div>
            </TabsContent>

            {/* Trade Score Engine - Phase 5 */}
            <TabsContent value="tradescore" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><TradeScoreQueue /></LazyTab>
              </div>
            </TabsContent>

            {/* Market Sentiment - 5 Layer Analysis v2 */}
            <TabsContent value="sentiment" className="mt-4" keepMounted>
              <div className="max-w-4xl mx-auto">
                <LazyTab><MarketSentimentV2 /></LazyTab>
              </div>
            </TabsContent>

            {/* REMOVED: Auto Trade Options */}

            <TabsContent value="analyzer" className="mt-4" keepMounted>
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Stock Scanner Section */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-cyan-400" />
                    Stock Scanner
                  </h2>
                  <LazyTab><StockAnalysisCard /></LazyTab>
                </div>
                
                {/* Top Options Opportunities Section */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-400" />
                    Options Opportunities
                  </h2>
                  <LazyTab><TopOpportunitiesScanner /></LazyTab>
                </div>
                
                {/* Options Levels Section */}
                <div>
                  <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-400" />
                    Options Levels & Support/Resistance
                  </h2>
                  <LazyTab><StockAnalyzer /></LazyTab>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="scanner" className="mt-4">
              <div className="max-w-4xl mx-auto space-y-6">
                {/* Fundamental Scanner - NEW */}
                <LazyTab><FundamentalScanner /></LazyTab>
                
                {/* Technical Scanner */}
                <LazyTab><AdvancedScanner /></LazyTab>
              </div>
            </TabsContent>

            <TabsContent value="leaderboard" className="mt-4">
              <div className="max-w-3xl mx-auto">
                <LazyTab><Leaderboard /></LazyTab>
              </div>
            </TabsContent>

            {/* Backtest - Admin Only */}
            {isAdmin && (
            <TabsContent value="backtest" className="mt-4">
              <div className="max-w-6xl mx-auto">
                <LazyTab><Backtesting /></LazyTab>
              </div>
            </TabsContent>
            )}

            {/* REMOVED: Journal */}

            {/* REMOVED: Paper Trading v2 */}

            {/* REMOVED: Alpaca Live Paper Trading */}

            {/* Smart Alerts - P1 */}
            <TabsContent value="alerts" className="mt-4" keepMounted>
              <div className="max-w-4xl mx-auto space-y-6">
                {/* High Conviction Alerts - Fundamentals + Technicals */}
                <LazyTab><HighConvictionAlerts /></LazyTab>
                
                {/* Liquidity Sweep Alerts - S&P 500 Daily/Weekly Sweeps */}
                <LazyTab><LiquiditySweepAlerts /></LazyTab>
                
                {/* Options Trade Tracker - Personal Win Rate */}
                <LazyTab><OptionsTradeTracker /></LazyTab>
                
                {/* OI Strike Heatmap - Shows Call Wall / Put Wall */}
                <LazyTab><OIStrikeHeatmap /></LazyTab>
                
                {/* OI Change Tracker - Smart Money Flow */}
                <LazyTab><OIChangeTracker /></LazyTab>
                
                {/* Implied Move Calculator */}
                <LazyTab><ImpliedMoveCalculator /></LazyTab>
                
                {/* Signal Tracker - Discord Signal Image Analysis */}
                <LazyTab><SignalTracker /></LazyTab>
                
                {/* Separator */}
                <div className="border-t border-slate-700 pt-6">
                  <h3 className="text-lg font-semibold text-zinc-600 mb-4">System Alerts</h3>
                  
                  {/* Sentiment Alerts - NEW P1 */}
                  <LazyTab><SentimentAlerts /></LazyTab>
                  
                  <LazyTab><SmartAlertSystem /></LazyTab>
                  {/* Active Alerts Summary */}
                  <div className="bg-white border border-zinc-200 rounded-sm p-4 shadow-sm mt-4" data-testid="alert-summary">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium">Legacy Alerts</span>
                      </div>
                      <span className="text-2xl font-mono font-medium tracking-tighter">{alerts.length}</span>
                    </div>
                  </div>
                  <LazyTab><AlertPanel alerts={alerts} /></LazyTab>
                </div>
              </div>
            </TabsContent>

            {/* Community Chat */}
            <TabsContent value="community" className="mt-4" keepMounted>
              <div className="max-w-3xl mx-auto">
                <LazyTab><CommunityChat /></LazyTab>
              </div>
            </TabsContent>

            {/* Economic Calendar - P1 */}
            <TabsContent value="calendar" className="mt-4">
              <div className="max-w-3xl mx-auto">
                <LazyTab><EconomicCalendar /></LazyTab>
              </div>
            </TabsContent>

            {/* Earnings Calendar Tab - Major Companies Earnings */}
            <TabsContent value="earnings" className="mt-4">
              <div className="max-w-5xl mx-auto">
                <LazyTab><EarningsCalendar /></LazyTab>
              </div>
            </TabsContent>

            {/* Market Recap Tab - Admin Only */}
            {isAdmin && (
            <TabsContent value="recap" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><MarketRecap /></LazyTab>
              </div>
            </TabsContent>
            )}

            {/* Stock Predictions Tab */}
            <TabsContent value="predictions" className="mt-4" keepMounted>
              <div className="max-w-6xl mx-auto">
                <LazyTab><StockPredictions /></LazyTab>
              </div>
            </TabsContent>

            {/* REMOVED: Polymarket Predictions Tab */}

            {/* Options Seller Tab */}
            <TabsContent value="optionsseller" className="mt-4" keepMounted>
              <div className="max-w-6xl mx-auto">
                <LazyTab><OptionsSellerPanel /></LazyTab>
              </div>
            </TabsContent>

            {/* REMOVED: Discipline Tab */}

            <TabsContent value="sweeps" className="mt-4">
              <div className="max-w-3xl mx-auto">
                <LazyTab><OptionsSweepsPanel /></LazyTab>
              </div>
            </TabsContent>
            
            <TabsContent value="options" className="mt-4">
              <LazyTab><OptionsFlowPanel /></LazyTab>
            </TabsContent>
            
            {/* VWAP Rejection Alerts - Multi-Timeframe Scanner */}
            <TabsContent value="vwaprejection" className="mt-4">
              <div className="max-w-6xl mx-auto">
                <LazyTab><VWAPRejectionAlerts /></LazyTab>
              </div>
            </TabsContent>
            
            <TabsContent value="darkpool" className="mt-4">
              <LazyTab><DarkPoolPanel /></LazyTab>
            </TabsContent>

            {/* REMOVED: Smart Money Confluence - Sweeps + Dark Pool Combined */}

            {/* Momentum Scanner - Breakout Stocks by Timeframe */}
            <TabsContent value="momentum" className="mt-4">
              <div className="max-w-5xl mx-auto">
                <LazyTab><MomentumScanner /></LazyTab>
              </div>
            </TabsContent>

            {/* Penny Runner Alerts - Low Float Momentum Plays */}
            <TabsContent value="pennyrunners" className="mt-4">
              <div className="max-w-6xl mx-auto">
                <LazyTab><PennyRunnerAlerts /></LazyTab>
              </div>
            </TabsContent>

            {/* AMD Phase Scanner - Wyckoff / SMC Analysis */}
            <TabsContent value="amdphases" className="mt-4">
              <div className="max-w-7xl mx-auto">
                <LazyTab><AMDPhaseScanner /></LazyTab>
              </div>
            </TabsContent>

            {/* SMC Pattern Alerts - FVG + Liquidity Sweep + BOS */}
            <TabsContent value="smcpatterns" className="mt-4">
              <div className="max-w-7xl mx-auto">
                <LazyTab><SMCPatternAlerts /></LazyTab>
              </div>
            </TabsContent>

            {/* Investment Portfolios - Risk-Based Strategies */}
            <TabsContent value="portfolios" className="mt-4">
              <div className="max-w-7xl mx-auto">
                <LazyTab><InvestmentPortfolios /></LazyTab>
              </div>
            </TabsContent>

            <TabsContent value="indiamarket" className="mt-4">
              <div className="max-w-7xl mx-auto">
                <LazyTab><IndiaMarket /></LazyTab>
              </div>
            </TabsContent>

            <TabsContent value="fiblevels" className="mt-4">
              <div className="max-w-5xl mx-auto">
                <LazyTab><FibLevels /></LazyTab>
              </div>
            </TabsContent>

            {/* Confluence Analyzer - Multi-Signal Conviction Scoring */}
            <TabsContent value="confluenceanalyzer" className="mt-4" keepMounted>
              <div className="max-w-5xl mx-auto">
                <LazyTab><ConfluenceAnalyzer /></LazyTab>
              </div>
            </TabsContent>
            
            <TabsContent value="insiders" className="mt-4">
              <LazyTab><InsiderTrading /></LazyTab>
            </TabsContent>
            
            <TabsContent value="institutional" className="mt-4">
              <div className="max-w-4xl mx-auto">
                <LazyTab><InstitutionalTrades /></LazyTab>
              </div>
            </TabsContent>
            
            {/* Live Signal Tracker - Real-Time Signal Performance */}
            <TabsContent value="livesignals" className="mt-4">
              <div className="max-w-7xl mx-auto">
                <LazyTab><LiveSignalTracker /></LazyTab>
              </div>
            </TabsContent>
            
            <TabsContent value="settings" className="mt-4">
              <div className="max-w-2xl mx-auto">
                <LazyTab><SettingsPanel /></LazyTab>
              </div>
            </TabsContent>

            {/* Help / User Guide */}
            <TabsContent value="help" className="mt-4">
              <div className="max-w-4xl mx-auto">
                <LazyTab><UserGuide /></LazyTab>
              </div>
            </TabsContent>
            
            {isAdmin && (
              <TabsContent value="admin" className="mt-4">
                <LazyTab><AdminPanel /></LazyTab>
              </TabsContent>
            )}
          </Tabs>

          {/* REMOVED: MarketOverview moved to Daily Picks tab only */}
        </div>

        {/* Disclaimer Footer */}
        <footer className="mt-8 mb-4 p-4 bg-zinc-100 border border-zinc-200 rounded-lg">
          <p className="text-xs text-zinc-600 text-center">
            <strong className="text-zinc-700">Disclaimer:</strong> TradeRelax provides market data and analysis for educational purposes only. 
            This is <strong>NOT financial advice</strong>. All trading involves risk. Past performance does not guarantee future results. 
            Always conduct your own research (DYOR) and consult a qualified financial advisor before making investment decisions. 
            The developers are not responsible for any trading losses.
          </p>
        </footer>
      </main>

      {/* AI Chat Widget - Floating */}
      <LazyTab><AIChatWidget /></LazyTab>

      {/* Onboarding Tour */}
      <LazyTab><OnboardingTour onComplete={handleOnboardingComplete} /></LazyTab>

      {/* REMOVED: Onboarding Checklist */}
    </div>
  );
}
